import { readFileSync } from "node:fs";
import { resolve } from "node:path";
import { StdioServerTransport } from "@modelcontextprotocol/sdk/server/stdio.js";

import {
  getWorkspaceId,
  bootstrap,
  orphanAgentSession,
  getAgentSessionId,
  recoverSessionState,
} from "./client.js";
import { initAnalytics, trackSessionStarted, shutdownAnalytics, getPostHogClient } from "./analytics.js";
import { initFeatureFlags } from "./featureFlags.js";
import { createProductBrainServer, SERVER_VERSION } from "./server.js";

try {
  const envPath = resolve(process.cwd(), ".env.mcp");
  for (const line of readFileSync(envPath, "utf-8").split("\n")) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("#")) continue;
    const eqIdx = trimmed.indexOf("=");
    if (eqIdx === -1) continue;
    process.env[trimmed.slice(0, eqIdx)] ??= trimmed.slice(eqIdx + 1);
  }
} catch {
  // .env.mcp not found — rely on env vars being set by the launcher
}

const withTimeout = <T>(p: Promise<T>, ms: number): Promise<T | void> =>
  Promise.race([p, new Promise<void>((r) => setTimeout(r, ms))]);

let shuttingDown = false;
async function gracefulShutdown() {
  if (shuttingDown) return;
  shuttingDown = true;
  process.stderr.write("[MCP] Graceful shutdown initiated.\n");
  // Hard-exit guard — fires if async cleanup exceeds budget.
  // Still exits 0: SIGTERM/stdin-close is an intentional shutdown regardless.
  const hardExit = setTimeout(() => {
    process.stderr.write("[MCP] Hard exit after shutdown timeout.\n");
    process.exit(0);
  }, 3_000);
  try {
    if (getAgentSessionId()) await withTimeout(orphanAgentSession(), 1_500);
  } catch {
    /* best-effort */
  }
  try {
    await withTimeout(shutdownAnalytics(), 1_000);
  } catch {
    /* best-effort */
  }
  clearTimeout(hardExit);
  process.exit(0);
}

// Register signal handlers BEFORE server.connect so they are always active
process.on("SIGINT", gracefulShutdown);
process.on("SIGTERM", gracefulShutdown);
process.on("unhandledRejection", (reason) => {
  const msg = reason instanceof Error ? reason.message : String(reason);
  process.stderr.write(`[MCP] Unhandled rejection: ${msg}\n`);
});
process.on("uncaughtException", (err) => {
  process.stderr.write(`[MCP] Uncaught exception: ${err.stack ?? err.message}\n`);
  // Track crash via PostHog so it surfaces in the analytics dashboard even without Sentry.
  getPostHogClient()?.capture({ distinctId: "mcp-server", event: "mcp_process_crashed", properties: { message: err.message, stack: err.stack } });
  gracefulShutdown();
});

bootstrap();
initAnalytics();
initFeatureFlags(getPostHogClient());

const server = createProductBrainServer();
const transport = new StdioServerTransport();
transport.onerror = (error) => {
  process.stderr.write(`[MCP] Transport error: ${error.message}\n`);
};
process.stderr.write("[MCP] Starting server.\n");
await server.connect(transport);
process.stderr.write("[MCP] Server connected.\n");

process.stdin.on("end", () => {
  gracefulShutdown();
});

// Do not auto-start agent sessions here: a second concurrent startAgentSession (e.g. from
// `session action=start` right after connect) can supersede the first session while client
// state still holds the stale id — markOriented then fails with "superseded, not active".
// Call `session action=start` or `start` explicitly (STD-135).
getWorkspaceId()
  .then(async (wsId) => {
    trackSessionStarted(wsId, SERVER_VERSION);
    try {
      await recoverSessionState();
    } catch {
      /* best-effort — fresh stdio has nothing to recover */
    }
  })
  .catch(() => {
    process.stderr.write("[MCP] Workspace resolution deferred — will retry on first tool call.\n");
  });
